<template>
  <div class="container new-book-container">
    <el-row>
      <el-col>
        <el-form :inline="true" size="mini" class="compact-form">
          <el-form-item>
            <el-button
                class="filter-item"
                type="primary"
                icon="el-icon-refresh"
                size="mini"
                @click="getList"
            >刷新</el-button>
          </el-form-item>
          <el-form-item>
            <el-button
                class="filter-item"
                type="info"
                icon="el-icon-download"
                size="mini"
                @click="handleDownload"
            >导出</el-button>
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <el-row v-loading="listLoading">
      <el-col :span="24">
        <div id="new-book-by-year-chart" class="chart-box"></div>
      </el-col>
    </el-row>
    <el-table
        :data="list"
        border
        fit
        size="mini"
        highlight-current-row
        show-summary
        :summary-method="getSummaries"
        class="compact-table"
        style="width: 100%;"
    >
      <el-table-column label="年份" align="center">
        <template v-slot="{row}">
          <span>{{ row.Year }}年</span>
        </template>
      </el-table-column>
      <el-table-column label="新增册数" align="center">
        <template v-slot="{row}">
          <span>{{ row.Total }}册</span>
        </template>
      </el-table-column>
      <el-table-column label="总价格" align="center">
        <template v-slot="{row}">
          <span>{{ formatPrice(row.Price) }}元</span>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import {ElMessage} from "element-plus";
import {BASE_URL} from "@/utils/constants";
import * as echarts from "echarts";
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';

export default {
  name: 'NewBookByYear',
  data() {
    return {
      list: [],
      listLoading: false,
      chart: null
    }
  },
  computed: {
    totalBooks() {
      return this.list.reduce((sum, item) => sum + (Number(item.Total) || 0), 0);
    },
    totalPrice() {
      const total = this.list.reduce((sum, item) => sum + (Number(item.Price) || 0), 0);
      return Number(total.toFixed(2));
    }
  },
  created() {
    this.getList();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    formatPrice(price) {
      const num = Number(price);
      return (isNaN(num) ? 0 : num).toFixed(2);
    },
    getList() {
      this.listLoading = true;
      this.$axios({
        url: BASE_URL + 'organizations/newbookbyyear',
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
          'dataType': 'json',
          'Authorization': localStorage.getItem("token")
        }
      }).then(res => {
        this.listLoading = false;
        if (res.data.ok) {
          this.list = Array.isArray(res.data.msg) ? res.data.msg : [];
          this.$nextTick(() => {
            this.renderChart();
          });
        } else {
          ElMessage.error(res.data.msg || '获取失败');
        }
      }).catch(() => {
        this.listLoading = false;
        ElMessage.error('获取失败');
      });
    },
    // 表格合计行
    getSummaries({columns}) {
      return columns.map((column, index) => {
        if (index === 0) return '合计';
        if (index === 1) return this.totalBooks + '册';
        if (index === 2) return this.totalPrice.toFixed(2) + '元';
        return '';
      });
    },
    renderChart() {
      const el = document.getElementById('new-book-by-year-chart');
      if (!el) return;
      if (!this.chart) {
        this.chart = echarts.init(el);
        window.addEventListener('resize', this.resizeChart);
      }
      // 接口按年份倒序返回,图表按年份正序展示趋势
      const ascList = [...this.list].reverse();
      const years = ascList.map(item => String(item.Year));
      const totals = ascList.map(item => Number(item.Total) || 0);
      const prices = ascList.map(item => Number(Number(item.Price || 0).toFixed(2)));
      this.chart.setOption({
        title: {
          text: '历年新增图书趋势',
          left: 'center',
          textStyle: {fontSize: 14}
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'cross'},
          formatter(params) {
            let html = params[0].axisValue + '年<br/>';
            params.forEach(p => {
              const unit = p.seriesName === '新增册数' ? '册' : '元';
              html += p.marker + p.seriesName + ':' + p.data + unit + '<br/>';
            });
            return html;
          }
        },
        legend: {
          data: ['新增册数', '总价格'],
          top: 25
        },
        grid: {
          left: 60,
          right: 70,
          top: 60,
          bottom: 30
        },
        xAxis: {
          type: 'category',
          data: years
        },
        yAxis: [
          {
            type: 'value',
            name: '册数',
            minInterval: 1
          },
          {
            type: 'value',
            name: '价格(元)'
          }
        ],
        series: [
          {
            name: '新增册数',
            type: 'bar',
            data: totals,
            barMaxWidth: 40,
            itemStyle: {color: '#409EFF'},
            label: {
              show: true,
              position: 'top'
            }
          },
          {
            name: '总价格',
            type: 'line',
            yAxisIndex: 1,
            data: prices,
            smooth: true,
            symbolSize: 8,
            itemStyle: {color: '#E6A23C'}
          }
        ]
      }, true);
    },
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    handleDownload() {
      if (!this.list || !this.list.length) {
        ElMessage.warning('没有数据可供导出');
        return;
      }
      const data = this.list.map(item => ({
        年份: item.Year + '年',
        新增册数: (Number(item.Total) || 0) + '册',
        总价格: this.formatPrice(item.Price) + '元'
      }));
      data.push({
        年份: '合计',
        新增册数: this.totalBooks + '册',
        总价格: this.totalPrice.toFixed(2) + '元'
      });
      try {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '历年新增图书');
        const wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([wbout], {type: 'application/octet-stream'});
        saveAs(blob, '历年新增图书.xlsx');
        ElMessage.success('导出成功');
      } catch (error) {
        ElMessage.error('导出失败: ' + error.message);
      }
    }
  }
}
</script>

<style scoped>
/* 紧凑布局,与其他报表页保持一致 */
.new-book-container {
  padding: 8px 10px 4px;
}
.new-book-container ::v-deep .compact-form .el-form-item {
  margin-right: 8px;
  margin-bottom: 2px;
}
.new-book-container ::v-deep .compact-form .el-form-item__label {
  padding-right: 6px;
}
.chart-box {
  width: 100%;
  height: 320px;
  margin-bottom: 8px;
}
.new-book-container ::v-deep .compact-table th,
.new-book-container ::v-deep .compact-table td {
  padding: 1px 0;
}
.new-book-container ::v-deep .compact-table .cell {
  line-height: 18px;
  padding-left: 6px;
  padding-right: 6px;
}
</style>
